import { getGlobalHeadersAsObject } from "../global-headers";

export interface InmailCostQueueItem {
	id: number;
	created: string;
	priority: number;
	type: string;
	profile_type: number;
	object_id: number;
	person_id: number | null;
	bot_name: string;
}

export interface InmailCostQueueResponse {
	count: number;
	next: string | null;
	previous: string | null;
	results: InmailCostQueueItem[];
}

export interface InmailCostEntry {
	id: number;
	person_id: number;
	person_name: string;
	user_username: string;
	cost: number;
	created: string;
}

export interface InmailCostResponse {
	count: number;
	next: string | null;
	previous: string | null;
	results: InmailCostEntry[];
}

export const fetchInmailCostQueue = async (
	limit: number = 100,
	offset: number = 0,
	personId?: number,
	user?: string[]
): Promise<InmailCostQueueResponse> => {
	let url = `/api/v2/inmail-cost-queue/?limit=${limit}&offset=${offset}`;
	if (personId) {
		url += `&object_id=${personId}`;
	}
	if (user && user.length > 0) {
		url += `&user=${user.join(",")}`;
	}
	const response = await fetch(url, {
		headers: { ...getGlobalHeadersAsObject() },
	});
	if (!response.ok) {
		throw new Error(`Failed to fetch InMail cost queue: ${response.statusText}`);
	}
	return response.json();
};

export const getInmailCostQueueUsers = async (): Promise<string[]> => {
	const response = await fetch("/api/v2/inmail-cost-queue/users/", {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	});
	if (!response.ok) {
		throw new Error(`Failed to fetch users: ${response.statusText}`);
	}
	return response.json();
};

export const deleteInmailCostQueueItem = async (itemId: number): Promise<void> => {
	const response = await fetch(`/api/v2/inmail-cost-queue/${itemId}/`, {
		method: "DELETE",
		headers: { ...getGlobalHeadersAsObject() },
	});
	if (!response.ok) {
		throw new Error(`Failed to delete queue item: ${response.statusText}`);
	}
};

export const addRandomInmailCostQueueItem = async (): Promise<InmailCostQueueItem> => {
	const response = await fetch("/api/v2/inmail-cost-queue/add-random/", {
		method: "POST",
		headers: { ...getGlobalHeadersAsObject() },
	});
	if (!response.ok) {
		const errorData = await response.json().catch(() => null);
		const errorMessage = errorData?.error || response.statusText;
		throw new Error(`Failed to add random queue item: ${errorMessage}`);
	}
	return response.json();
};

export const fetchRecentInmailCosts = async (
	limit: number = 50,
	offset: number = 0,
	personId?: number,
	user?: string[]
): Promise<InmailCostResponse> => {
	let url = `/api/v2/inmail-cost-queue/recent-costs/?limit=${limit}&offset=${offset}`;
	if (personId) {
		url += `&person_id=${personId}`;
	}
	if (user && user.length > 0) {
		url += `&user=${user.join(",")}`;
	}
	const response = await fetch(url, {
		headers: { ...getGlobalHeadersAsObject() },
	});
	if (!response.ok) {
		throw new Error(`Failed to fetch InMail costs: ${response.statusText}`);
	}
	return response.json();
};

export interface PersonInmailCostData {
	queue_items: InmailCostQueueItem[];
	cost_results: InmailCostEntry[];
}

export const fetchPersonInmailCostData = async (
	personId: number
): Promise<PersonInmailCostData> => {
	const url = `/api/v2/inmail-cost-queue/person/${personId}/`;
	const response = await fetch(url, {
		headers: { ...getGlobalHeadersAsObject() },
	});
	if (!response.ok) {
		throw new Error(
			`Failed to fetch person InMail cost data: ${response.statusText}`
		);
	}
	return response.json();
};
